import cn from 'classnames'
import {FC, ReactNode} from 'react'

import {Text, TextFontWeight, TextVariant} from '@/components/common/content/Text'
import {theme} from '@/theme/theme'
import {MOBILE_SCREENSIZE} from '@/utils/styles/breakpoints'

export enum BadgeColor {
  blue = 'blue',
  green = 'green',
  red = 'red',
  yellow = 'yellow',
  butter = 'butter',
  gray = 'gray',
}

export const badgeColorToPalette = {
  [BadgeColor.blue]: theme.palette.info,
  [BadgeColor.green]: theme.palette.success,
  [BadgeColor.red]: theme.palette.error,
  [BadgeColor.yellow]: theme.palette.warning,
  [BadgeColor.butter]: {
    light: theme.palette.butter[100],
    contrastText: theme.palette.yellow[800],
    outlineColor: theme.palette.butter[400],
  },
  [BadgeColor.gray]: {
    light: theme.palette.gray[100],
    contrastText: theme.palette.gray[900],
    outlineColor: theme.palette.gray[300],
  },
}

export enum BadgeSize {
  small = 'small',
  medium = 'medium',
}

type BadgeProps = {
  title: string
  color?: BadgeColor
  icon?: ReactNode
  iconPosition?: 'left' | 'right'
  size?: BadgeSize
}

export const Badge: FC<BadgeProps> = ({
  title,
  color = BadgeColor.gray,
  icon,
  iconPosition = 'left',
  size = BadgeSize.small,
}) => {
  const palette = badgeColorToPalette[color]
  const textColor = palette.contrastText
  const backgroundColor = palette.light
  const borderColor = palette.outlineColor
  const textVariant = size === BadgeSize.small ? TextVariant.text14 : TextVariant.text16

  return (
    <>
      <div className={cn('badge', size)}>
        {icon}
        <Text variant={textVariant} fontWeight={TextFontWeight.medium} color={textColor}>
          {title}
        </Text>
      </div>

      <style jsx>{`
        .badge {
          display: inline-flex;
          align-items: center;
          flex-direction: ${iconPosition === 'left' ? 'row' : 'row-reverse'};
          background-color: ${backgroundColor};
          padding: ${theme.spacing[0.5]} ${theme.spacing[1.5]};
          border-radius: 4px;
          box-shadow: inset 0px 0px 0px 1px ${borderColor};
          gap: ${theme.spacing[1]};
        }
        .medium {
          padding: ${theme.spacing[2]};
          border-radius: 8px;
        }
        @media ${MOBILE_SCREENSIZE} {
          .medium {
            padding: ${theme.spacing[1.5]} ${theme.spacing[2]};
          }
        }
      `}</style>
    </>
  )
}
